import React, { useState } from 'react';
import { Card, CardTitle } from '@raptormaps/card';
import { Button } from '@raptormaps/button';
import { Text } from '@raptormaps/text';
import { Row, Stack } from '@raptormaps/layout';
import { SolarFarm } from '../../api/schemas/SolarFarmSchema';
import { formatDate } from '../../utils/general_utils';
import { Icon } from '@raptormaps/icon';
import './FarmDetailCard.css';

export function FarmDetailCard({
  farm,
  loading,
  onEditClick,
}: {
  farm: SolarFarm;
  loading: boolean;
  onEditClick: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const copyUuid = () => {
    if (farm.uuid) {
      navigator.clipboard.writeText(farm.uuid);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const statusLabel =
    farm.site_status ?? (farm.status != null ? String(farm.status) : null);

  return (
    <div className="fd-wrapper">
      <div className="fd-page-header">
        <Stack gutter="xxs">
          <Text variant="label_small_allcaps" color="#718096">
            FARMS › DETAILS
          </Text>
          <Row gutter="sm" align="center">
            <Text variant="h3" as="h1">
              {farm.name ?? 'Unnamed Farm'}
            </Text>
            {statusLabel && (
              <span
                className={
                  statusLabel.toLowerCase() === 'commissioned'
                    ? 'fd-badge fd-badge--green'
                    : 'fd-badge fd-badge--blue'
                }
              >
                {statusLabel.toUpperCase()}
              </span>
            )}
          </Row>
        </Stack>
        <div className="fd-page-header-right">
          {!loading && (
            <Button variant="primary" onClick={onEditClick}>
              ✎ Edit Farm
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="fd-loading">
          <Text variant="paragraph_medium" color="#718096">
            Loading farm details...
          </Text>
        </div>
      ) : (
        <div className="fd-grid">
          {/* Row 1: Identification + Capacity */}
          <div className="fd-row fd-row--top">
            <Card
              className="fd-card--identification"
              gutter="none"
              style={{ overflow: 'hidden' }}
            >
              <div className="fd-card-header">
                <span className="fd-card-icon">
                  <Icon icon="User" />
                </span>
                <CardTitle>Identification</CardTitle>
              </div>
              <div className="fd-card-body">
                <div className="fd-id-top">
                  <Stack gutter="xxs">
                    <Text variant="label_small_allcaps" color="#718096">
                      Farm Name
                    </Text>
                    <Text variant="paragraph_medium">{farm.name ?? '—'}</Text>
                  </Stack>
                  <Stack gutter="xxs">
                    <Text variant="label_small_allcaps" color="#718096">
                      Farm ID
                    </Text>
                    <Text variant="paragraph_medium">
                      {farm.id != null ? String(farm.id) : '—'}
                    </Text>
                  </Stack>
                  <Stack gutter="xxs">
                    <Text variant="label_small_allcaps" color="#718096">
                      External ID (asset id)
                    </Text>
                    <Text variant="paragraph_medium">
                      {farm.asset_id ?? '—'}
                    </Text>
                  </Stack>
                </div>
                {farm.access_code != null && (
                  <Stack gutter="xxs">
                    <Text variant="label_small_allcaps" color="#718096">
                      Access Code
                    </Text>
                    <Text variant="paragraph_medium">{farm.access_code}</Text>
                  </Stack>
                )}
                <Stack gutter="xxs">
                  <Text variant="label_small_allcaps" color="#718096">
                    System UUID
                  </Text>
                  <div className="fd-uuid-box">
                    <span className="fd-uuid-text">{farm.uuid ?? '—'}</span>
                    <button
                      className="fd-copy-btn"
                      onClick={copyUuid}
                      aria-label="Copy UUID"
                      title="Copy UUID"
                    >
                      <Icon icon="Copy" />
                      {copied && <span className="fd-copied">Copied!</span>}
                    </button>
                  </div>
                </Stack>
              </div>
            </Card>

            <div className="fd-card fd-card--capacity">
              <Text variant="label_small_allcaps" color="#2563eb">
                Capacity Output
              </Text>
              <div className="fd-capacity-value">
                <span className="fd-capacity-number">
                  {farm.size != null ? farm.size : '—'}
                </span>
                {farm.size != null && (
                  <span className="fd-capacity-unit">MW</span>
                )}
              </div>
              {farm.ac_size_megawatts != null && (
                <div className="fd-efficiency">
                  <div className="fd-efficiency-row">
                    <span className="fd-efficiency-label">AC Capacity</span>
                    <span className="fd-efficiency-pct">
                      {farm.ac_size_megawatts} MW
                    </span>
                  </div>
                  <div className="fd-progress-track">
                    <div
                      className="fd-progress-fill"
                      style={{
                        width: `${Math.min(
                          100,
                          ((farm.ac_size_megawatts ?? 0) / (farm.size ?? 1)) *
                            100,
                        )}%`,
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Row 2: Location */}
          <Card
            className="fd-card--location"
            gutter="none"
            style={{ overflow: 'hidden' }}
          >
            <div className="fd-card-header">
              <span className="fd-card-icon">
                <Icon icon="Map" />
              </span>
              <CardTitle>Location Details</CardTitle>
            </div>
            <div className="fd-card-body">
              <div className="fd-location-inner">
                <div className="fd-location-fields">
                  <div className="fd-location-row">
                    <Stack gutter="xxs">
                      <Text variant="label_small_allcaps" color="#718096">
                        Country
                      </Text>
                      <Text variant="paragraph_medium">
                        {farm.country ?? '—'}
                      </Text>
                    </Stack>
                    <Stack gutter="xxs">
                      <Text variant="label_small_allcaps" color="#718096">
                        State
                      </Text>
                      <Text variant="paragraph_medium">
                        {farm.state ?? '—'}
                      </Text>
                    </Stack>
                  </div>
                  <Stack gutter="xxs" style={{ marginTop: '0.75rem' }}>
                    <Text variant="label_small_allcaps" color="#718096">
                      Address
                    </Text>
                    <Text variant="paragraph_medium">
                      {farm.address ?? '—'}
                    </Text>
                  </Stack>
                  <table className="fd-coords-table">
                    <tbody>
                      <tr>
                        <td className="fd-coords-key">Latitude</td>
                        <td className="fd-coords-val">
                          {farm.latitude != null ? farm.latitude : '—'}
                        </td>
                      </tr>
                      <tr>
                        <td className="fd-coords-key">Longitude</td>
                        <td className="fd-coords-val">
                          {farm.longitude != null ? farm.longitude : '—'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="fd-map-placeholder">
                  {farm.latitude != null && farm.longitude != null ? (
                    <a
                      className="fd-map-link"
                      href={`https://www.google.com/maps?q=${farm.latitude},${farm.longitude}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Icon icon="LocationDot" size="large" color="#2563eb" />
                      <span>
                        {farm.latitude.toFixed(5)}, {farm.longitude.toFixed(5)}
                      </span>
                      <span className="fd-map-link-sub">
                        Open in Google Maps ↗
                      </span>
                    </a>
                  ) : (
                    <div className="fd-map-no-coords">
                      <Text variant="paragraph_small" color="#718096">
                        No coordinates available
                      </Text>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>

          {/* Row 3: Org + Site Specifics + System History */}
          <div className="fd-row fd-row--three">
            <Card gutter="none" style={{ overflow: 'hidden' }}>
              <div className="fd-card-header">
                <span className="fd-card-icon">
                  <Icon icon="House" />
                </span>
                <CardTitle>Organization</CardTitle>
              </div>
              <div className="fd-card-body">
                <div className="fd-org-field">
                  <Text variant="label_small_allcaps" color="#718096">
                    Org ID
                  </Text>
                  <Text variant="paragraph_medium" color="#2563eb">
                    {farm.org_id != null ? farm.org_id : '—'}
                  </Text>
                </div>
                <div className="fd-org-field">
                  <Text variant="label_small_allcaps" color="#718096">
                    Client Org ID
                  </Text>
                  <Text variant="paragraph_medium" color="#2563eb">
                    {farm.client_org_id != null ? farm.client_org_id : '—'}
                  </Text>
                </div>
                <div className="fd-org-field">
                  <Text variant="label_small_allcaps" color="#718096">
                    Creator ID
                  </Text>
                  <Text variant="paragraph_medium" color="#2563eb">
                    {farm.creator_id != null ? farm.creator_id : '—'}
                  </Text>
                </div>
              </div>
            </Card>

            <Card gutter="none" style={{ overflow: 'hidden' }}>
              <div className="fd-card-header">
                <span className="fd-card-icon">
                  <Icon icon="ChartLineUpDown" />
                </span>
                <CardTitle>Site Specifics</CardTitle>
              </div>
              <div className="fd-card-body">
                <Stack gutter="md">
                  <Stack gutter="xxs">
                    <Text variant="label_small_allcaps" color="#718096">
                      PPA Rate
                    </Text>
                    <div className="fd-spec-value">
                      {farm.ppa_rate != null ? (
                        <>
                          <span className="fd-spec-big">
                            ${farm.ppa_rate.toFixed(2)}
                          </span>
                          <span className="fd-spec-unit">/kWh</span>
                        </>
                      ) : (
                        <span className="fd-spec-big">—</span>
                      )}
                    </div>
                  </Stack>
                  <Stack gutter="xxs">
                    <Text variant="label_small_allcaps" color="#718096">
                      Peak Sunlight
                    </Text>
                    <div className="fd-spec-value">
                      <span className="fd-spec-big">
                        {farm.peak_sunlight_hours_annual != null
                          ? farm.peak_sunlight_hours_annual
                          : '—'}
                      </span>
                      {farm.peak_sunlight_hours_annual != null && (
                        <span className="fd-spec-unit"> hrs</span>
                      )}
                    </div>
                  </Stack>
                </Stack>
              </div>
            </Card>

            <Card gutter="none" style={{ overflow: 'hidden' }}>
              <div className="fd-card-header">
                <span className="fd-card-icon">
                  <Icon icon="Clock" />
                </span>
                <CardTitle>System History</CardTitle>
              </div>
              <div className="fd-card-body">
                <div className="fd-timeline">
                  <div className="fd-timeline-item">
                    <div className="fd-timeline-dot fd-timeline-dot--blue" />
                    <div className="fd-timeline-content">
                      <Text variant="label_small_allcaps" color="#718096">
                        Created
                      </Text>
                      <div className="fd-timeline-date">
                        {formatDate(farm.created_tsecs)}
                      </div>
                      <div className="fd-timeline-note">
                        System initialization
                      </div>
                    </div>
                  </div>
                  <div className="fd-timeline-item">
                    <div className="fd-timeline-dot fd-timeline-dot--green" />
                    <div className="fd-timeline-content">
                      <Text variant="label_small_allcaps" color="#718096">
                        Commissioned
                      </Text>
                      <div className="fd-timeline-date">
                        {formatDate(farm.commissioning_tsecs)}
                      </div>
                      <div className="fd-timeline-note">
                        Grid interconnection
                      </div>
                    </div>
                  </div>
                  <div className="fd-timeline-item">
                    <div className="fd-timeline-dot fd-timeline-dot--gray" />
                    <div className="fd-timeline-content">
                      <Text variant="label_small_allcaps" color="#718096">
                        Last Updated
                      </Text>
                      <div className="fd-timeline-date">
                        {formatDate(farm.updated_tsecs)}
                      </div>
                      <div className="fd-timeline-note">Last modification</div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Row 4: Module Configuration */}
          <Card
            className="fd-card--modules"
            gutter="none"
            style={{ overflow: 'hidden' }}
          >
            <div className="fd-card-header">
              <span className="fd-card-icon">
                <Icon icon="LayerGroup" />
              </span>
              <CardTitle>Module Configuration</CardTitle>
            </div>
            <div className="fd-card-body">
              <div className="fd-modules-row">
                <div className="fd-module-tile">
                  <div className="fd-module-icon">
                    <Icon icon="BorderInner" size="medium" />
                  </div>
                  <Stack gutter="xxs">
                    <Text variant="label_small_allcaps" color="#718096">
                      Layout
                    </Text>
                    <div className="fd-module-val">
                      {farm.module_layout ?? '—'}
                    </div>
                  </Stack>
                </div>
                <div className="fd-module-tile">
                  <div className="fd-module-icon">
                    <Icon icon="Gear" size="medium" />
                  </div>
                  <Stack gutter="xxs">
                    <Text variant="label_small_allcaps" color="#718096">
                      Mounting
                    </Text>
                    <div className="fd-module-val">
                      {farm.module_mount
                        ? farm.module_mount.toUpperCase()
                        : '—'}
                    </div>
                  </Stack>
                </div>
                {farm.site_layout && (
                  <div className="fd-module-tile">
                    <div className="fd-module-icon">
                      <Icon icon="VectorSquare" size="medium" />
                    </div>
                    <Stack gutter="xxs">
                      <Text variant="label_small_allcaps" color="#718096">
                        Site Layout
                      </Text>
                      <div className="fd-module-val">{farm.site_layout}</div>
                    </Stack>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
